import React, { useState, useEffect, useCallback } from 'react';
import { news } from '../mockData';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const NewsCarousel = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Mostrar las 4 noticias más recientes ordenadas por fecha
  const latestNews = [...news]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 4);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % latestNews.length);
  }, [latestNews.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + latestNews.length) % latestNews.length);
  }, [latestNews.length]);

  // Auto-play cada 4 segundos
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(nextSlide, 4000);
      return () => clearInterval(interval);
    }
  }, [isPaused, nextSlide]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleNewsClick = (slug) => {
    navigate(`/noticias/${slug}`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short', 
      year: 'numeric'
    });
  };

  return (
    <section className="news-carousel-section" data-testid="news-carousel-section">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-2">ÚLTIMAS NOTICIAS</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle body-medium">
            Mantente al día con las novedades de nuestros jugadores
          </p>
        </div>

        <div 
          className="news-carousel-container"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          data-testid="news-carousel-container"
        >
          <button 
            className="carousel-btn carousel-btn-prev" 
            onClick={prevSlide}
            aria-label="Anterior noticia"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="news-carousel-wrapper">
            {latestNews.map((item, index) => (
              <div
                key={item.id}
                className={`news-carousel-slide ${index === currentSlide ? 'active' : ''}`}
                onClick={() => handleNewsClick(item.slug)}
                data-testid={`news-carousel-slide-${index}`}
              >
                <div className="news-carousel-image-container">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="news-carousel-image"
                  />
                  <div className="news-carousel-overlay">
                    <span className="news-category">{item.category}</span>
                  </div>
                </div>
                <div className="news-carousel-content">
                  <div className="news-date">
                    <Calendar size={14} />
                    <span className="caption">{formatDate(item.date)}</span>
                  </div>
                  <h3 className="heading-5">{item.title}</h3>
                  <p className="body-small">{item.summary}</p>
                  <span className="news-read-more">Leer más →</span>
                </div>
              </div>
            ))}
          </div>

          <button 
            className="carousel-btn carousel-btn-next" 
            onClick={nextSlide}
            aria-label="Siguiente noticia"
            data-testid="carousel-next-btn"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots indicator */}
          <div className="carousel-dots">
            {latestNews.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Ir a noticia ${index + 1}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Link a todas las noticias */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <Link to="/noticias" className="btn-primary" data-testid="see-all-news-btn">
            VER TODAS LAS NOTICIAS
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewsCarousel;
